import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import styled from '@emotion/styled';
import { uiTheme as t, FormDrawer, TextField } from 'Components/ui';
import useTranslation from 'Hooks/useTranslation';
import { usersAccountApi } from 'Api/axios/users-account-api';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  employeeId: string;
  /** Cuando un administrador restablece, no se pide la contraseña actual. */
  isAdmin: boolean;
  onClose: () => void;
}

const Fields = styled.div({ display: 'flex', flexDirection: 'column', gap: 14 });

const Field = styled.label({ display: 'flex', flexDirection: 'column', gap: 6 });

const FieldText = styled.span({ fontFamily: t.font, fontSize: 13, fontWeight: 600, color: t.color.title });

interface FormState {
  currentPassword: string;
  newPassword: string;
  repeatNewPassword: string;
}

const EMPTY: FormState = { currentPassword: '', newPassword: '', repeatNewPassword: '' };

/**
 * Restablecimiento de contraseña, en el cajón lateral.
 *
 * Habla con `PATCH /users/:id/password`. Un administrador no necesita la
 * contraseña actual de la persona; quien cambia la propia sí la da, igual que
 * antes.
 */
const ResetPasswordDrawer = ({ open, employeeId, isAdmin, onClose }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setErrors({});
    }
  }, [open]);

  const handleChange = (field: keyof FormState) => (value: string) => {
    setForm(previous => ({ ...previous, [field]: value }));
  };

  const validate = (): boolean => {
    const next: Partial<FormState> = {};

    if (!isAdmin && form.currentPassword.length === 0) {
      next.currentPassword = t2('ErrorPasswordBlank') as string;
    }

    if (form.newPassword.length < 6) {
      next.newPassword = t2('ErrorPasswordLength') as string;
    }

    if (form.newPassword !== form.repeatNewPassword) {
      next.repeatNewPassword = t2('ErrorPasswordsDontMatch') as string;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    const response = await usersAccountApi.updatePassword(employeeId, {
      oldPassword: isAdmin ? undefined : form.currentPassword,
      newPassword: form.newPassword,
      isAdmin,
    });
    setSaving(false);

    if (response.error) {
      toast.error(response.error);
      return;
    }

    toast.success(t2('AlertSuccessChangePassword') as string);
    onClose();
  };

  return (
    <FormDrawer
      open={open}
      formId="reset-password-form"
      title={t2('ResetPassword') as string}
      subtitle={t2('reset_password_subtitle') as string}
      labels={{ close: t2('Close') as string, cancel: t2('Cancel') as string, submit: t2('Reset') as string }}
      submitting={saving}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <Fields>
        {!isAdmin ? (
          <Field>
            <FieldText>{t2('CurrentPassword')}</FieldText>
            <TextField
              id="current-password"
              type="password"
              value={form.currentPassword}
              onChange={handleChange('currentPassword')}
              error={errors.currentPassword}
            />
          </Field>
        ) : null}
        <Field>
          <FieldText>{t2('NewPassword')}</FieldText>
          <TextField
            id="new-password"
            type="password"
            value={form.newPassword}
            onChange={handleChange('newPassword')}
            error={errors.newPassword}
          />
        </Field>
        <Field>
          <FieldText>{t2('RepeatNewPassword')}</FieldText>
          <TextField
            id="repeat-password"
            type="password"
            value={form.repeatNewPassword}
            onChange={handleChange('repeatNewPassword')}
            error={errors.repeatNewPassword}
          />
        </Field>
      </Fields>
    </FormDrawer>
  );
};

export default ResetPasswordDrawer;
